import React, {useMemo} from 'react';
import {tokens, view} from '@sail/ui';
import type {Intent} from '@sail/engine';
import {createKeyframes, useTokenValue} from '@sail/engine';
import {
  getNeutralSkeleton100ValueFromBackground,
  getNeutralSkeleton150ValueFromBackground,
} from '@stripe-internal/embedded-theming';

type Props = {
  /**
   * Height of the component.
   */
  height?: string;
  /**
   * Width of the component.
   */
  width?: string;
  /**
   * Maximum width of the component.
   */
  maxWidth?: string;
  /**
   * If true, the Skeleton component should have a width of 100% to fill its parent container
   */
  fillWidth?: boolean;
  /**
   * Vertical margin of the Skeleton
   */
  margin?: number;
  /**
   * Custom styles
   */
  styles?: Intent[];
};

const skeletonLoading = createKeyframes({
  0: {
    backgroundPosition: '200% center',
  },
  100: {
    backgroundPosition: '0 center',
  },
});

export const Skeleton: React.FC<Props> = ({
  height = '14px',
  margin = 0,
  width,
  maxWidth,
  fillWidth,
  styles,
}) => {
  // We derive these colors from the background
  const backgroundValue = useTokenValue(tokens.color.background.surface);
  const neutral100DerivedValue = useMemo(
    () => getNeutralSkeleton100ValueFromBackground(backgroundValue),
    [backgroundValue],
  );

  const neutral150DerivedValue = useMemo(
    () => getNeutralSkeleton150ValueFromBackground(backgroundValue),
    [backgroundValue],
  );
  return (
    <view.div
      data-testid="skeleton-loading"
      uses={[...(styles || [])]}
      css={{
        borderRadius: 'small',
        animation: `${skeletonLoading} 1400ms ease-in-out 0ms infinite both`,
        width: fillWidth || !width ? 'fill' : width,
        ...(maxWidth !== undefined ? {maxWidth} : {}),
        height,
        margin: `${margin}px 0px`,
      }}
      style={{
        backgroundImage: `linear-gradient(to right, ${neutral100DerivedValue} 50%, ${neutral150DerivedValue} 80%, ${neutral100DerivedValue} 100%)`,
        backgroundSize: '200% 100%',
      }}
    />
  );
};
